/* ============================================================
   MEDIA LIBRARY screen — grid of uploaded images with an upload tile.
   In picker mode (opened from an image slot in the editor) selecting a
   tile assigns it and returns to the editor. Otherwise selecting opens
   a detail panel for alt text and delete.
   ============================================================ */
import { useRef, useState } from 'react'
import { useMediaLibrary } from './useMediaLibrary.js'
import { Icon } from '../components/Icon.jsx'

function Thumb({ item }) {
  // Real upload (url) → cover image; legacy gradient seed → css background.
  if (item.url) {
    return (
      <div className="mthumb" style={{ background: `center / cover no-repeat url(${item.url})` }} />
    )
  }
  return <div className="mthumb" style={{ background: item.css || '#100f0d' }} />
}

export function MediaLibrary({ picking, onPick }) {
  const { media, busy, upload, setAlt, remove } = useMediaLibrary()
  const [sel, setSel] = useState(null)
  const [err, setErr] = useState(null)
  const fileRef = useRef(null)

  const selected = media.find((m) => m.id === sel)

  async function onFiles(e) {
    setErr(null)
    const files = Array.from(e.target.files || [])
    e.target.value = '' // allow re-selecting the same file
    for (const f of files) {
      try {
        const id = await upload(f)
        setSel(id)
      } catch (er) {
        setErr(er.message || 'Upload failed.')
      }
    }
  }

  return (
    <div>
      {picking && (
        <div className="picker-hint">
          <Icon name="image" size={18} />
          <span>Choose an image to assign — it returns you to the editor.</span>
        </div>
      )}
      {err && <div className="picker-hint err">{err}</div>}

      <div className="media-grid">
        <button className="media-upload" onClick={() => fileRef.current?.click()} disabled={busy}>
          <Icon name={busy ? 'ring' : 'plus'} size={22} />
          <span>{busy ? 'UPLOADING…' : 'UPLOAD'}</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />
        {media.map((m) => (
          <button
            key={m.id}
            className={'media-tile' + (sel === m.id ? ' sel' : '')}
            onClick={() => (picking ? onPick(m.id) : setSel(m.id))}
          >
            <Thumb item={m} />
            <div className="mov"></div>
            <div className="mcap">{m.name}</div>
            {!m.alt && m.url && (
              <div className="mflag" title="Missing alt text">
                !
              </div>
            )}
          </button>
        ))}
      </div>

      {!picking && selected && (
        <div className="media-detail">
          <Thumb item={selected} />
          <div className="md-body">
            <div className="md-name">{selected.name}</div>
            <div className="ff">
              <label>Alt text (for accessibility &amp; SEO)</label>
              <input
                value={selected.alt || ''}
                placeholder="Describe the image"
                onChange={(e) => setAlt(selected.id, e.target.value)}
              />
            </div>
            {selected.width ? (
              <div className="md-dim">
                {selected.width} × {selected.height} · {selected.type}
              </div>
            ) : null}
            <button
              className="btn-ghost"
              onClick={() => {
                remove(selected)
                setSel(null)
              }}
            >
              Delete image
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
